import PropTypes from 'prop-types';
import { useMemo } from 'react';
import moment from 'moment';
import { Col, Row, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import { ActionButtons, TagTable, SpaceActionButtons } from '../Style';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';
import { DataTable } from '../../../components/dataTable/data-table';
import { SearchButtonComponent } from '../../../components/buttons/search-button/SearchButton';
import { BulletIcon } from '../../../components/utilities/appIcons';
import { useColorList } from '../../../hooks/useColorList';
import { ModalServiceEvent, useModalServiceEvent } from '../../../components/modalServiceEvent/ModalServiceEvent';
import { finalCustomerSolutionIdRules } from '../../../config/constants/finalCustomerSolutionRuleConstants';
import { getFullName } from '../../../utility/finalCustomerUtils';

export const PendingsBoardTable = ({ tableData, isLoading, settingData, getPendingsSettings }) => {
  const { translate: t } = useDictionaryTranslation();
  const solutionId = useSelector((state) => state.auth?.accountData?.solutionId);
  const finalCustomerSolutionRules = finalCustomerSolutionIdRules[solutionId] ?? finalCustomerSolutionIdRules.default;
  const {
    selectedServiceData,
    handleEditService,
    onHanldeCloseModalEdit,
    handleCancelService,
    closeModalCancel,
    handleOpenModal,
    handleCloseModal,
  } = useModalServiceEvent({
    onSaveData: () => getPendingsSettings(),
  });

  const { getColorById } = useColorList();

  const changeRowColor = (record) => {
    return {
      style: { background: record.colorHighlight },
    };
  };

  const bulletedText = (text, color, tooltipText) => {
    return (
      <>
        <Tooltip title={tooltipText}>
          <>{color ? <BulletIcon color={color ?? '#fffff'} /> : null}</>
        </Tooltip>
        {text && <span>{text}</span>}
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        key: 'status',
        title: t('status'),
        show: settingData?.fields.status,
        render: (_, record) => (
          <>
            <TagTable tagColor={record.infoStatus?.color}>{record.infoStatus?.name}</TagTable>
          </>
        ),
      },
      {
        key: 'id',
        dataIndex: 'privateId',
        title: t('Id'),
        show: settingData?.fields.id,
        width: '5%',
      },
      {
        key: 'name',
        dataIndex: 'name',
        title: t('serviceName'),
        show: settingData?.fields.name,
        width: '18%',
      },
      {
        key: 'customer',
        title: t('customer'),
        show: settingData?.fields.customer,
        render: (_, record) => record.infoAssignedCompany?.name,
      },
      {
        key: 'finalCustomer',
        title: t('finalCustomer'),
        show: settingData?.fields.finalCustomer,
        render: (_, record) => 
          getFullName(record.infoFinalCustomer, {
            useFullName: finalCustomerSolutionRules.showName1,
          }),
        width: '18%',
      },
      {
        key: 'serviceType',
        title: t('serviceType'),
        show: settingData?.fields.serviceType,
        render: (_, record) =>
          bulletedText(record.infoServiceType?.name, getColorById(record.infoServiceType?.idColor)),
        width: '18%',
      },
      {
        key: 'assignedDate',
        title: t('assignedDateOverview'),
        show: settingData?.fields.assignedDate,
        render: (_, record) => moment(record.start).format('DD/MM/YYYY HH:mm'),
        dataIndex: 'start',
        sorter: (a, b) => a.start.localeCompare(b.start),
        width: '10%',
      },
      {
        key: 'creationDate',
        title: t('creationDate'),
        show: settingData?.fields.creationDate,
        render: (_, record) => moment(record.createdAt).format('DD/MM/YYYY HH:mm'),
        dataIndex: 'createdAt',
        sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
        width: '10%',
      },
      {
        key: 'technician',
        title: t('technician'),
        show: settingData?.fields.assignedUser,
        render: (_, record) => record.infoAssignedUser?.profile.name,
        width: '8%',
      },
      {
        key: 'serviceTags',
        title: t('tags'),
        show: settingData?.fields.serviceTags,
        render: (_, record) => {
          return (
            <Row gutter={[2, 2]} wrap>
              {record.serviceTags.map((item) => {
                return (
                  <Col key={item?._id}>
                    {bulletedText(null, getColorById(item.idColor), `${item.label} (${item.value})`)}
                  </Col>
                );
              })}
            </Row>
          );
        },
        width: '6%',
      },
      {
        show: true,
        render: (_, record) => (
          <SpaceActionButtons>
            <Link to={`/services-detail/${record?._id}/summary`}>
              <SearchButtonComponent primaryColor="lightgrey" textColor="black" width="25px" />
            </Link>
            <ActionButtons
              onClick={() => handleOpenModal(record._id)}
              icon={<EditOutlined className="icon" />}
              style={{ background: '#44BFF0', color: 'white' }}
            />
          </SpaceActionButtons>
        ),
        width: '3%',
      },
    ],
    [settingData],
  );

  return (
    <>
      <DataTable
        rowKey="_id"
        columns={columns.filter((column) => column.show)}
        isLoading={isLoading}
        tableData={tableData.data}
        showPagination={false}
        onRow={changeRowColor}
        headerData={{
          show: false,
        }}
      />
      <ModalServiceEvent
        selectedServiceData={selectedServiceData}
        handleEditService={handleEditService}
        onHanldeCloseModalEdit={onHanldeCloseModalEdit}
        handleCancelService={handleCancelService}
        closeModalCancel={closeModalCancel}
        handleCloseModal={handleCloseModal}
      />
    </>
  );
};

PendingsBoardTable.propTypes = {
  settingData: PropTypes.object,
  tableData: PropTypes.shape({
    data: PropTypes.object,
    getData: PropTypes.func,
  }),
  isLoading: PropTypes.bool,
  getPendingsSettings: PropTypes.func,
};
